/**
 * 日中相談（daytime advice）の検索ページ用に、検索条件を History API の state に保存・復元するユーティリティ。
 *
 * - 保存: 検索が完了したタイミングで「最後に実行した検索条件」を history.state に保存する
 * - 復元: 検索ページへ（再）入った際、保存済みの状態があれば常に復元して裏で再検索する
 * - 補足: 検索結果（sheets）自体は保存しない（漏えいリスクを下げる）
 *
 * 注意:
 * vue-router(createWebHistory) は内部で history.state を使用する。
 * そのため、このユーティリティは state を上書きせず、必ず「既存stateにマージして replaceState」する。
 */
export const DAYTIME_ADVICE_SEARCH_HISTORY_STATE_VERSION = 1;

export const DAYTIME_ADVICE_SEARCH_PERSISTED_STATE_KEY = "__daytimeAdviceSearchLastExecuted";

export type DaytimeAdviceSearchCondition = {
  date: string | null;
  status: number | null;
  doctorFamilyName: string | null;
  doctorFirstName: string | null;
};

export type DaytimeAdviceSearchPersistedState = {
  version: number;
  savedAt: number;
  condition: DaytimeAdviceSearchCondition;
};

type HistoryStateObject = Record<string, unknown>;

const safeHistory = (): History | null => {
  try {
    return window?.history ?? null;
  } catch {
    return null;
  }
};

const readHistoryState = (): HistoryStateObject | null => {
  const h = safeHistory();
  if (!h) return null;
  try {
    const s = h.state;
    if (!s || typeof s !== "object") return {};
    return s as HistoryStateObject;
  } catch {
    return null;
  }
};

const replaceHistoryState = (nextPartialState: HistoryStateObject): void => {
  const h = safeHistory();
  if (!h) return;
  const current = readHistoryState();
  if (!current) return;

  // vue-router が持つ state を壊さないようにマージする
  const merged = {
    ...current,
    ...nextPartialState
  };

  h.replaceState(merged, "");
};

const removeHistoryStateKeys = (keys: string[]): void => {
  const h = safeHistory();
  if (!h) return;

  const current = readHistoryState();
  if (!current) return;

  const next: HistoryStateObject = { ...current };
  for (const key of keys) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete next[key];
  }
  h.replaceState(next, "");
};

export const clearPersistedState = (): void => {
  removeHistoryStateKeys([DAYTIME_ADVICE_SEARCH_PERSISTED_STATE_KEY]);
};

export const persistExecutedSearchState = (state: Omit<DaytimeAdviceSearchPersistedState, "version" | "savedAt">): void => {
  const payload: DaytimeAdviceSearchPersistedState = {
    version: DAYTIME_ADVICE_SEARCH_HISTORY_STATE_VERSION,
    savedAt: Date.now(),
    condition: state.condition
  };

  replaceHistoryState({
    [DAYTIME_ADVICE_SEARCH_PERSISTED_STATE_KEY]: payload
  });
};

export const loadPersistedState = (): DaytimeAdviceSearchPersistedState | null => {
  const current = readHistoryState();
  if (!current) return null;
  const payload = current[DAYTIME_ADVICE_SEARCH_PERSISTED_STATE_KEY] as DaytimeAdviceSearchPersistedState | undefined;

  if (!payload || typeof payload !== "object") return null;
  if (payload.version !== DAYTIME_ADVICE_SEARCH_HISTORY_STATE_VERSION) return null;
  if (!payload.condition) return null;

  return payload;
};
